const BASE = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';

/**
 * Tidal current at the Golden Gate.
 *
 * `SFB1203` (Golden Gate Bridge, 0.46 nm E) is a harmonic station, so it carries
 * continuous predictions rather than offsets from somewhere else. It reports
 * `Velocity_Major` signed in knots — positive floods, negative ebbs — plus the
 * mean set of each phase: flood 069°, ebb 257°.
 *
 * These are PREDICTIONS, not observations. Everything derived from them is
 * labelled as such in the UI; the wind readings are the only measured data here.
 */
export const GATE_CURRENT_STATION = 'SFB1203';

/** Predictions are stable for the day, so each station is fetched at most once. */
const cache = new Map<string, Promise<CurrentSet>>();

export type CurrentPhase = 'ebb' | 'flood' | 'slack';

export interface CurrentEvent {
  t: Date;
  kind: CurrentPhase;
  /** Signed knots at a max; ~0 at slack. */
  velocityKt: number;
}

export interface CurrentSet {
  events: CurrentEvent[];
  /** Mean direction the flood sets toward, degrees true. */
  floodDir: number;
  /** Mean direction the ebb sets toward, degrees true. */
  ebbDir: number;
}

export interface CurrentState {
  phase: CurrentPhase;
  /** Whether the stream is still making or already easing off. */
  trend: 'building' | 'easing';
  /** Magnitude of the governing max, in knots. */
  peakKt: number;
  peakAt: Date;
  nextSlack: Date | null;
  /** Direction the stream sets toward for the current phase, degrees true. */
  setDeg: number;
}

/** Hours of already-run stream kept for context before "now". */
const LOOKBEHIND_H = 3;
/** Total window, leaving 33 h ahead so a full day of future is always visible. */
const WINDOW_H = 36;

export interface CurrentCurve {
  /** 30-minute predicted velocity along the major axis. Positive floods. */
  points: { t: Date; v: number }[];
  /** Max flood, max ebb and slack events inside the window. */
  events: CurrentEvent[];
  floodDir: number;
  ebbDir: number;
}

const curveCache = new Map<string, Promise<CurrentCurve>>();

function windowStart(now: Date): Date {
  const d = new Date(now.getTime() - LOOKBEHIND_H * 3600_000);
  d.setMinutes(0, 0, 0);
  return d;
}

function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * The continuous velocity curve for one station, plus its slack and maximum
 * events — the same 36-hour window the tide chart uses.
 *
 * Cached per station and per window start; the hourly key lets a long session
 * roll forward rather than showing an ageing window.
 */
export function currentCurve(stationId: string, now: Date = new Date()): Promise<CurrentCurve> {
  const begin = windowStart(now);
  const key = `${stationId}:${begin.toISOString()}`;

  const hit = curveCache.get(key);
  if (hit) return hit;

  const pending = fetchCurve(stationId, begin).catch((err) => {
    curveCache.delete(key);
    throw err;
  });
  curveCache.set(key, pending);
  return pending;
}

async function fetchCurve(stationId: string, begin: Date): Promise<CurrentCurve> {
  const base = {
    product: 'currents_predictions',
    units: 'english',
    time_zone: 'lst_ldt',
    format: 'json',
    application: 'sfbaywind',
    station: stationId,
    begin_date: stamp(begin),
    range: String(WINDOW_H),
  };

  /** CO-OPS answers 403 to a burst; one paced retry clears it. */
  const get = async (interval: string, attempt = 0): Promise<Record<string, string>[]> => {
    const res = await fetch(`${BASE}?${new URLSearchParams({ ...base, interval })}`);
    if (!res.ok) {
      if (attempt === 0 && (res.status === 403 || res.status === 429 || res.status >= 500)) {
        await new Promise((r) => setTimeout(r, 900));
        return get(interval, attempt + 1);
      }
      throw new Error(`Currents HTTP ${res.status}`);
    }
    const body = await res.json();
    if (body.error) throw new Error(body.error.message ?? 'No current predictions');
    return body?.current_predictions?.cp ?? [];
  };

  const [curveRows, eventRows] = await Promise.all([get('30'), get('MAX_SLACK')]);
  if (curveRows.length === 0) throw new Error('No current predictions');

  return {
    points: curveRows.map((r) => ({
      t: new Date(String(r.Time).replace(' ', 'T')),
      v: Number(r.Velocity_Major),
    })),
    events: eventRows.map((r) => ({
      t: new Date(String(r.Time).replace(' ', 'T')),
      kind: String(r.Type).toLowerCase() as CurrentPhase,
      velocityKt: Number(r.Velocity_Major),
    })),
    floodDir: Number(curveRows[0].meanFloodDir),
    ebbDir: Number(curveRows[0].meanEbbDir),
  };
}

function ymd(d: Date): string {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Slack and max-flood/max-ebb events spanning yesterday through tomorrow, so a
 * lookup near midnight still finds an event on both sides of now.
 */
export function currentsFor(stationId: string, now: Date = new Date()): Promise<CurrentSet> {
  const hit = cache.get(stationId);
  if (hit) return hit;

  const pending = fetchCurrents(stationId, now).catch((err) => {
    cache.delete(stationId); // Let a transient failure be retried.
    throw err;
  });
  cache.set(stationId, pending);
  return pending;
}

export function gateCurrents(now: Date = new Date()): Promise<CurrentSet> {
  return currentsFor(GATE_CURRENT_STATION, now);
}

async function fetchCurrents(stationId: string, now: Date): Promise<CurrentSet> {
  const from = new Date(now.getTime() - 24 * 3600_000);
  const to = new Date(now.getTime() + 24 * 3600_000);

  const qs = new URLSearchParams({
    product: 'currents_predictions',
    interval: 'MAX_SLACK',
    units: 'english',
    time_zone: 'lst_ldt',
    format: 'json',
    application: 'sfbaywind',
    // The bare id resolves to the same depth bin the Bar Pilots quote; the
    // `_NN` suffix on their links is a web-viewer artefact the API rejects.
    station: stationId,
    begin_date: ymd(from),
    end_date: ymd(to),
  });

  const res = await fetch(`${BASE}?${qs}`);
  if (!res.ok) throw new Error(`Currents HTTP ${res.status}`);

  const body = await res.json();
  const cp = body?.current_predictions?.cp;
  if (!Array.isArray(cp) || cp.length === 0) throw new Error('No current predictions');

  const events: CurrentEvent[] = cp.map((row: Record<string, string | number>) => ({
    // lst_ldt is local wall-clock at the station, which is our display zone too.
    t: new Date(String(row.Time).replace(' ', 'T')),
    kind: String(row.Type).toLowerCase() as CurrentPhase,
    velocityKt: Number(row.Velocity_Major),
  }));

  return {
    events: events.sort((a, b) => a.t.getTime() - b.t.getTime()),
    floodDir: Number(cp[0].meanFloodDir),
    ebbDir: Number(cp[0].meanEbbDir),
  };
}
